import vendorStatusService from "../../../../services/procurementService/vendorStatusService";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { CACHE_KEY_VENDORSTATUSES } from "../../../../utils/constants";
import { VendorStatusInterface } from "../../../../models/ProcurementModule/VendorStatus";

interface UpdateVendorStatusContext {
  previousVendorStatuses: VendorStatusInterface[];
}

const useUpdateVendorStatus = (page: number, pageSize: number) => {
  const queryClient = useQueryClient();

  return useMutation<
    VendorStatusInterface,
    Error,
    VendorStatusInterface,
    UpdateVendorStatusContext
  >({
    mutationFn: async (vendorStatus: VendorStatusInterface) => {
      // Assuming the ID is stored in the vendorStatus object or can be derived
      const { id, ...vendorStatusData } = vendorStatus;
      return vendorStatusService.Update(id || "", vendorStatusData);
    },

    onMutate: async (updatedVendorStatus: VendorStatusInterface) => {
      await queryClient.cancelQueries({
        queryKey: [CACHE_KEY_VENDORSTATUSES, page, pageSize],
      });

      const previousVendorStatuses =
        queryClient.getQueryData<VendorStatusInterface[]>([
          CACHE_KEY_VENDORSTATUSES,
          page,
          pageSize,
        ]) || [];

      queryClient.setQueryData<VendorStatusInterface[]>(
        [CACHE_KEY_VENDORSTATUSES, page, pageSize],
        (vendorstatuses = []) =>
          vendorstatuses.map((vendorstatus) =>
            vendorstatus.id === updatedVendorStatus.id
              ? updatedVendorStatus
              : vendorstatus
          )
      );

      return { previousVendorStatuses };
    },

    onSuccess: (savedVendorStatus) => {
      queryClient.setQueryData<VendorStatusInterface[]>(
        [CACHE_KEY_VENDORSTATUSES, page, pageSize],
        (vendorstatuses) =>
          vendorstatuses?.map((vendorstatus) =>
            vendorstatus.id === savedVendorStatus.id
              ? savedVendorStatus
              : vendorstatus
          )
      );
    },

    onError: (error, updatedVendorStatus, context) => {
      if (!context) return;

      queryClient.setQueryData<VendorStatusInterface[]>(
        [CACHE_KEY_VENDORSTATUSES, page, pageSize],
        context.previousVendorStatuses
      );
    },
  });
};

export default useUpdateVendorStatus;
